/**
 * Flair (ADR-0160): what a mythic piece does on its own, round the gardener who wears it: flames off a burning skull, a
 * cape and nine tails that swing as their gardener moves, a ring of stars turning round a head, bolts jumping over a
 * suit of storm plate. One engine steps it and hands out a flat list of squares and glows to draw (`draws`), so the game
 * (Pixi), the menus' gardeners and the admin app's Skins page (Canvas 2D) show the very same thing.
 *
 * Everything is in the gardener's art px (a quarter of a world px, ADR-0073): a point of the body's 64×72 frame standing
 * facing right (`Anchor`), simulated in the world (what is shed stays where it was shed, what hangs lags behind) and
 * drawn from the feet, on whole art px, so it sits on the body's own pixel grid. Colours are the palette's (or the
 * team's: `Tone`); a glow is the atlas's `skin_glow`, added.
 *
 * Presentation only (CLAUDE.md rule 5): nothing here reaches the sim, and its randomness is its own (`Rand`).
 */

/** A colour: RGB, or the wearer's team's (its mid tone, its light, its dark). */
export type Tone = number | 'team' | 'teamLight' | 'teamDark';

const TEAM_TONES: Record<'blue' | 'red', Record<Exclude<Tone, number>, number>> = {
  blue: { team: 0x0099db, teamLight: 0x2ce8f5, teamDark: 0x124e89 },
  red: { team: 0xe43b44, teamLight: 0xf6757a, teamDark: 0xa22633 },
};

/**
 * A point on a gardener: of the body's 64×72 frame standing, facing right (`on` says what it rides: the feet stay put,
 * the torso bobs, the head bobs, breathes and leans), or of the held gun's 64×36 frame (`gun`: its grip at [16, 20], its
 * muzzle at [64, 20]; it turns with the aim).
 */
export interface Anchor {
  on: 'feet' | 'torso' | 'head' | 'gun';
  x: number;
  y: number;
  /**
   * Rides boughs that bend as their gardener moves (a tree's crown): above `row` of the frame it is pushed against the
   * gardener's moves (setting off left, it leans right; stopping, it swings back past its rest and settles), `k` art px
   * for every px it stands over `row` at a run. A chain bends along its length.
   */
  bend?: readonly [row: number, k: number];
}

/** Behind the body, over it, or with the held gun (over it wherever it is drawn). */
export type FlairLayer = 'back' | 'front' | 'gun';

/** What is shed from a point: sparks, flames, petals, rain. Speeds in art px/s, x forward (mirrored with the facing). */
export interface Emit {
  kind: 'emit';
  at: Anchor;
  /** Shed from anywhere in this box round the point. */
  box?: readonly [w: number, h: number];
  /** A second. */
  rate: number;
  /** More of them per art px run (a trail). */
  run?: number;
  v: readonly [vx0: number, vx1: number, vy0: number, vy1: number];
  /** How much of the gardener's own speed it starts with (0: left behind, 1: carried along). */
  carry?: number;
  /** Gravity, art px/s² (negative: it rises). */
  g?: number;
  /** The share of its speed it keeps a second (1: all). */
  drag?: number;
  /** Pulled to the point it came from, art px/s² (the void drinks them in). */
  pull?: number;
  /** A sideways wobble: px, Hz. */
  wobble?: readonly [amp: number, hz: number];
  life: readonly [min: number, max: number];
  /** Its side, art px, born to gone; `tall`: so many times as high as wide (rain). */
  size: readonly [from: number, to: number];
  tall?: number;
  /** Its colour over its life. */
  tones: readonly Tone[];
  alpha?: readonly [from: number, to: number];
  add?: boolean;
  /** A glow under it, this many times its size. */
  glow?: number;
  layer: FlairLayer;
}

/** What `Emit` says but where from: for what is shed off a chain's end or an orbiting light. */
export type Shed = Omit<Emit, 'kind' | 'at' | 'layer' | 'run'>;

/**
 * Something that hangs and swings: a cape, a scarf, a tail, a charm on a string. A chain of links from a point, each
 * pulled to its place in the chain's rest shape and dragged by its own weight and speed, so it lags its gardener and
 * swings back.
 */
export interface Chain {
  kind: 'chain';
  at: Anchor;
  /** Links, and a link's length (art px). */
  n: number;
  seg: number;
  /** Where it points at rest, radians (0 forward, π/2 down, π back), each link `curl` more. */
  dir: number;
  curl?: number;
  /** How hard it holds its rest shape, a second (2 limp cloth … 30 a stiff tail). */
  stiff: number;
  g?: number;
  drag?: number;
  /** Its rest direction swings: radians, Hz, where in the swing it starts (0..1). */
  sway?: readonly [amp: number, hz: number, phase: number];
  /** A flutter running down it, art px/s². */
  wind?: number;
  /** Its width at the root, the middle and the tip (art px). */
  w: readonly [root: number, mid: number, tip: number];
  /** `dots`: a round tail or cord; `bars`: cloth, a row of pixels a link. */
  shape: 'dots' | 'bars';
  /** Root to tip. */
  tones: readonly Tone[];
  /** Its 2-px rim (the body's sel-out weight). */
  rim?: Tone;
  /** A lit strip down its left side (cloth) or its top (a tail). */
  lit?: Tone;
  /** Cloth torn at its end into so many teeth. */
  tatter?: number;
  /** Shed from its last links. */
  shed?: Shed;
  layer: FlairLayer;
}

/** Lights going round a point on a tilted ring: behind the body on its far half, over it on the near one. */
export interface Orbit {
  kind: 'orbit';
  at: Anchor;
  rx: number;
  ry: number;
  /** The ring's tilt, radians. */
  tilt?: number;
  n: number;
  /** Seconds a turn (negative: the other way). */
  period: number;
  /** Each one's side (art px) and colours (the first its core; more: a tail of smaller ones behind it). */
  size: number;
  tones: readonly Tone[];
  /** How far apart on the ring a light's tail squares are, radians. */
  tail?: number;
  glow?: number;
  /** Each is a flame of this height and half-width (art px) in `tones` (its edge, its body, its heart), not a square. */
  flame?: readonly [h: number, w: number];
  /** Each bobs: px, Hz. */
  bob?: readonly [amp: number, hz: number];
  /** Uneven: each one's radius off by up to this share, its brightness flickering. */
  rough?: number;
  shed?: Shed;
  /** Always on this layer (default: behind on the far half, in front on the near one). */
  layer?: FlairLayer;
}

/** Bolts jumping between two of `pts` every so often. */
export interface Arcs {
  kind: 'arcs';
  pts: readonly Anchor[];
  every: readonly [min: number, max: number];
  /** Seconds a bolt shows. */
  life: number;
  /** How far it strays from the straight line, art px. */
  jag: number;
  tones: readonly [core: Tone, halo: Tone];
  glow?: number;
  layer: FlairLayer;
}

/** Stars coming and going in a box (an ellipse, with `round`) at a point, drifting. */
export interface Twinkle {
  kind: 'twinkle';
  at: Anchor;
  box: readonly [w: number, h: number];
  round?: boolean;
  n: number;
  /** Seconds each takes to come and go. */
  period: readonly [min: number, max: number];
  tones: readonly Tone[];
  drift?: readonly [vx: number, vy: number];
  add?: boolean;
  layer: FlairLayer;
}

/**
 * A tongue of fire standing on a point: a pixel flame drawn row by row (its edge, its body, its heart), flickering,
 * leaning as it wavers and blown back when its gardener runs.
 */
export interface Flame {
  kind: 'flame';
  at: Anchor;
  /** Its height and its half-width at its widest, art px. */
  h: number;
  w: number;
  /** Outside in: its edge, its body, its heart. */
  tones: readonly [Tone, Tone, Tone];
  /** How fast it flickers (1 a candle … 3 a blaze). */
  speed?: number;
  /** How far its tip is blown back, art px for each art px/s its gardener runs. */
  blown?: number;
  /** Shed off its tip. */
  shed?: Shed;
  layer: FlairLayer;
}

/** A glow breathing at a point. */
export interface Pulse {
  kind: 'pulse';
  at: Anchor;
  /** Its radius, art px, and its strength, between breaths. */
  r: readonly [min: number, max: number];
  alpha: readonly [min: number, max: number];
  period: number;
  tone: Tone;
  layer: FlairLayer;
}

/**
 * A little picture of pixels at a point, one of `frames` at a time (ADR-0160 amended): a clock's hands ticking round, a
 * visor's face changing, a hieroglyph glowing and going. Each frame is rows of characters (a sprite grid's: `.`, or any
 * character `tones` lacks, is nothing), each one's colour in `tones`; drawn with its top-left `at`, on whole art px
 * (mirrored with the facing, as everything is; on a gun it rides the aim but isn't turned with it).
 */
export interface Glyph {
  kind: 'glyph';
  at: Anchor;
  frames: readonly string[];
  tones: Readonly<Record<string, Tone>>;
  /** Seconds a frame shows: each a hold between these, drawn afresh (equal: a steady beat). */
  hold: readonly [min: number, max: number];
  /** `cycle`: the frames in order; `random`: any other frame next. */
  order?: 'cycle' | 'random';
  /** Shown only so much of each hold (0..1, from its start; default all): a sign that flashes and goes. */
  shown?: number;
  /** Each frame somewhere new in this box round the point (a hieroglyph here, then there); none: on the point. */
  box?: readonly [w: number, h: number];
  /** A glow under its middle: its radius (art px) and strength, in the first of `tones`. */
  glow?: readonly [r: number, a: number];
  add?: boolean;
  layer: FlairLayer;
}

export type FlairPart = Emit | Chain | Orbit | Arcs | Twinkle | Pulse | Flame | Glyph;
/** A piece's flair: its parts, drawn in this order within a layer. */
export type FlairSpec = readonly FlairPart[];

/** How the body stands on a frame of its animation: the torso's bob, the head's (with its breath), the head's lean. */
export interface BodyPose {
  bob: number;
  head: number;
  lean: number;
}

export interface FlairPose {
  /** The feet in the world, art px (world px × 4). */
  x: number;
  y: number;
  facing: 1 | -1;
  body: BodyPose;
  /** The held gun's grip from the feet (art px) and its aim; null: no gun flair (another gun in hand, or none). */
  gun: { x: number; y: number; aim: number } | null;
  team: 'blue' | 'red';
  /**
   * The body's squash from its feet (`PlayerView`'s spring: 0.78 hurt, a little on each shot; its width 2 − this; none:
   * 1): what rides the body squashes with it (a visor's face stays on the visor), what was shed goes its own way.
   */
  squash?: number;
}

/** Something to draw, from the feet in art px (x right, y down): a square (`w` × `h` from its top-left) or a glow (centred). */
export interface FlairDraw {
  layer: 0 | 1 | 2;
  glow: boolean;
  x: number;
  y: number;
  w: number;
  h: number;
  rgb: number;
  a: number;
  add: boolean;
}

/**
 * A glow's strength by how far out it is (0 its middle … 1 its edge): four rings, as the atlas's `skin_glow` (a glow in
 * pixels, never a smooth falloff). Whoever draws flair paints its glow from this.
 */
export const glowAt = (r: number): number => (r < 0.37 ? 0.75 : r < 0.61 ? 0.55 : r < 0.83 ? 0.35 : r < 1 ? 0.23 : 0);
/** A glow's picture is this many art px across (`skin_glow`'s). */
export const GLOW_PX = 36;

const LAYER = { back: 0, front: 1, gun: 2 } as const;
/** The body's frame: its feet. */
const FEET = [32, 71] as const;
/** The gun's frame: its grip (`pollinator`'s origin). */
const GRIP = [16, 20] as const;
/** Particles a gardener may have at once: the oldest go first. */
const MAX_BITS = 220;
/** A step's longest dt (a tab back from the background): beyond it the chains would whip. */
const MAX_DT = 1 / 20;
/** A move this far in a step (art px) is a teleport: nothing trails, the chains come along. */
const JUMP = 160;
/** What bends (`Anchor.bend`): a spring after the gardener's speed (a run's, art px/s, leans it 1), its Hz, its damping. */
const BEND = { run: 300, hz: 1.7, damp: 0.3 } as const;

/** Small and its own (xorshift32): flair never draws from the game's randomness. */
class Rand {
  private s: number;
  constructor(seed: number) {
    this.s = seed | 0 || 0x9e3779b9;
  }
  next(): number {
    let s = this.s;
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    this.s = s;
    return (s >>> 0) / 4294967296;
  }
  in(a: number, b: number): number {
    return a + (b - a) * this.next();
  }
}

interface Bit {
  spec: Shed;
  layer: 0 | 1 | 2;
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Where it was shed from (the world), for `pull`. */
  hx: number;
  hy: number;
  age: number;
  life: number;
  seed: number;
}

interface ChainState {
  /** Its links' places in the world, and where they were a step ago (x, y pairs). */
  p: Float32Array;
  q: Float32Array;
  shed: number;
  /** Cloth: it streams sideways (drawn in columns) rather than hangs (rows). */
  across: boolean;
  /** Its root from the feet, eased after its anchor. */
  ax: number;
  ay: number;
}

interface Bolt {
  /** Its path from the feet (x, y pairs), and how long it has shown. */
  path: number[];
  age: number;
}

/** A glyph's frame: its pixels (column, row, character) and its size. */
interface GlyphFrame {
  px: (readonly [x: number, y: number, ch: string])[];
  w: number;
  h: number;
}
const GLYPHS = new WeakMap<Glyph, GlyphFrame[]>();
/** `g`'s frames read once a glyph (its rows' indentation ignored, as a sprite grid's is). */
function framesOf(g: Glyph): GlyphFrame[] {
  let frames = GLYPHS.get(g);
  if (!frames) {
    frames = g.frames.map((frame) => {
      const rows = frame
        .split('\n')
        .map((row) => row.trim())
        .filter((row) => row.length > 0);
      const px = rows.flatMap((row, y) =>
        [...row].flatMap((ch, x) => (Object.hasOwn(g.tones, ch) ? [[x, y, ch] as const] : [])),
      );
      return { px, w: Math.max(0, ...rows.map((row) => row.length)), h: rows.length };
    });
    GLYPHS.set(g, frames);
  }
  return frames;
}

const mix = (a: number, b: number, t: number): number => {
  const ar = (a >> 16) & 255;
  const ag = (a >> 8) & 255;
  const ab = a & 255;
  const r = ar + (((b >> 16) & 255) - ar) * t;
  const g = ag + (((b >> 8) & 255) - ag) * t;
  const bl = ab + ((b & 255) - ab) * t;
  return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(bl);
};

/** A gardener's flair, stepped every frame it is drawn (`step`), then drawn from `draws` (its first `count`). */
export class Flair {
  readonly draws: FlairDraw[] = [];
  count = 0;
  private readonly parts: readonly FlairPart[];
  private readonly rand: Rand;
  private readonly bits: Bit[] = [];
  /** Per part: what it carries between steps. */
  private readonly owed: number[];
  private readonly chains = new Map<number, ChainState>();
  private readonly bolts = new Map<number, { next: number; live: Bolt[] }>();
  private readonly stars = new Map<number, Float32Array>();
  /** Per glyph: its frame, the seconds left of its hold and the whole hold, where in its box it stands. */
  private readonly signs = new Map<number, { frame: number; left: number; hold: number; u: number; v: number }>();
  private time = 0;
  private last: { x: number; y: number } | null = null;
  private pose: FlairPose | null = null;
  /** The gardener's speed, smoothed (art px/s): what a flame leans from. */
  private wind = 0;
  /** How far what bends leans (a run's worth 1), and how fast it is swinging. */
  private lean = 0;
  private leanV = 0;
  private tones = TEAM_TONES.blue;

  constructor(specs: readonly FlairSpec[], seed = 1) {
    this.parts = specs.flat();
    this.rand = new Rand(seed);
    this.owed = this.parts.map(() => this.rand.next());
    this.time = this.rand.next() * 10;
  }

  /**
   * A frame on: `pose` is where the gardener is and how it stands. `on` false (knocked out, in a Mecha Suit): nothing new
   * is shed and what hangs is put away, what was shed lives out its life.
   */
  step(dt: number, pose: FlairPose, on = true): void {
    const h = Math.min(Math.max(dt, 0), MAX_DT);
    this.time += h;
    this.pose = pose;
    this.tones = TEAM_TONES[pose.team];
    let [mx, my] = this.last ? [pose.x - this.last.x, pose.y - this.last.y] : [0, 0];
    const jumped = Math.hypot(mx, my) > JUMP;
    if (jumped) {
      // Teleported: what trailed comes along instead of streaking across the map.
      for (const b of this.bits) {
        b.x += mx;
        b.y += my;
        b.hx += mx;
        b.hy += my;
      }
      for (const c of this.chains.values())
        for (let i = 0; i < c.p.length; i += 2) {
          c.p[i] = (c.p[i] as number) + mx;
          c.p[i + 1] = (c.p[i + 1] as number) + my;
          c.q[i] = (c.q[i] as number) + mx;
          c.q[i + 1] = (c.q[i + 1] as number) + my;
        }
      mx = my = 0;
    }
    this.last = { x: pose.x, y: pose.y };
    const moved = Math.hypot(mx, my);
    const [gvx, gvy] = h > 0 ? [mx / h, my / h] : [0, 0];
    this.wind += (gvx - this.wind) * Math.min(1, h * 7);
    // What bends leans against the gardener's speed, a spring after it: it lags a start and swings past a stop.
    const w = BEND.hz * Math.PI * 2;
    const lean = Math.max(-1.5, Math.min(1.5, -gvx / BEND.run));
    this.leanV += ((lean - this.lean) * w * w - 2 * BEND.damp * w * this.leanV) * h;
    this.lean += this.leanV * h;

    this.count = 0;
    this.parts.forEach((part, i) => {
      const gunless = part.kind !== 'arcs' && part.at.on === 'gun' && !pose.gun;
      const live = on && !gunless && !(part.kind === 'arcs' && !pose.gun && part.pts.some((p) => p.on === 'gun'));
      if (part.kind === 'emit') {
        if (!live) return;
        this.owed[i] = (this.owed[i] as number) + part.rate * h + (part.run ?? 0) * moved;
        while ((this.owed[i] as number) >= 1) {
          this.owed[i] = (this.owed[i] as number) - 1;
          const [ax, ay] = this.at(part.at);
          this.shed(part, LAYER[part.layer], ax, ay, gvx, gvy);
        }
      } else if (part.kind === 'chain') {
        if (!live) {
          this.chains.delete(i);
          return;
        }
        this.swing(i, part, h, gvx, gvy);
      } else if (part.kind === 'orbit') {
        if (live) this.turn(i, part, h, gvx, gvy);
      } else if (part.kind === 'arcs') {
        if (!live) {
          this.bolts.delete(i);
          return;
        }
        this.strike(i, part, h);
      } else if (part.kind === 'twinkle') {
        if (live) this.shine(i, part);
      } else if (part.kind === 'flame') {
        if (live) this.burn(i, part, h, gvx, gvy);
      } else if (part.kind === 'glyph') {
        if (live) this.sign(i, part, h);
      } else if (live) this.breathe(part);
    });
    this.age(h);
  }

  // --- Where things are ------------------------------------------------------------------------------------------------

  /** How far a point at `row` of the frame is pushed by what bends (`Anchor.bend`), art px (world x). */
  private bent(bend: Anchor['bend'], row: number): number {
    return bend ? this.lean * bend[1] * Math.max(0, bend[0] - row) : 0;
  }

  /**
   * `a` from the feet (art px, x right on the screen): the facing mirrors it, its part's bob and lean move it, and what
   * bends bends from its row `down` px under it (a picture's middle).
   */
  private at(a: Anchor, down = 0): [number, number] {
    const pose = this.pose as FlairPose;
    const f = pose.facing;
    if (a.on === 'gun') {
      const gun = pose.gun ?? { x: 4 * f, y: -16, aim: f > 0 ? 0 : Math.PI };
      const [dx, dy] = [a.x - GRIP[0], (a.y - GRIP[1]) * f];
      const [c, s] = [Math.cos(gun.aim), Math.sin(gun.aim)];
      return [gun.x + dx * c - dy * s, gun.y + dx * s + dy * c];
    }
    const b = pose.body;
    const lean = a.on === 'head' ? b.lean : 0;
    const bob = a.on === 'head' ? b.head : a.on === 'torso' ? b.bob : 0;
    const sq = pose.squash ?? 1;
    return [(a.x - FEET[0] + lean) * f * (2 - sq) + this.bent(a.bend, a.y + down), (a.y - FEET[1] + bob) * sq];
  }

  private rgb(t: Tone): number {
    return typeof t === 'number' ? t : this.tones[t];
  }

  /** The colour `at` (0..1) of the way along `tones`, blended between neighbours. */
  private ramp(tones: readonly Tone[], at: number): number {
    const k = Math.min(0.9999, Math.max(0, at)) * (tones.length - 1);
    const i = Math.floor(k);
    const a = this.rgb(tones[i] as Tone);
    return tones.length === 1 ? a : mix(a, this.rgb(tones[Math.min(tones.length - 1, i + 1)] as Tone), k - i);
  }

  // --- Drawing ---------------------------------------------------------------------------------------------------------

  private put(
    layer: 0 | 1 | 2,
    glow: boolean,
    x: number,
    y: number,
    w: number,
    h: number,
    rgb: number,
    a: number,
    add: boolean,
  ) {
    if (a <= 0.01 || w <= 0 || h <= 0) return;
    let d = this.draws[this.count];
    if (!d) {
      d = { layer, glow, x, y, w, h, rgb, a, add };
      this.draws.push(d);
    } else Object.assign(d, { layer, glow, x, y, w, h, rgb, a, add });
    this.count++;
  }

  /** A square of side `s` (`tall` times as high) centred on (x, y), on whole art px. */
  private dot(layer: 0 | 1 | 2, x: number, y: number, s: number, rgb: number, a = 1, add = false, tall = 1): void {
    const w = Math.max(1, Math.round(s));
    const h = Math.max(1, Math.round(s * tall));
    this.put(layer, false, Math.round(x - w / 2), Math.round(y - h / 2), w, h, rgb, a, add);
  }

  private halo(layer: 0 | 1 | 2, x: number, y: number, r: number, rgb: number, a: number): void {
    this.put(layer, true, x, y, r * 2, r * 2, rgb, a, true);
  }

  // --- Particles -------------------------------------------------------------------------------------------------------

  /** One of `spec` shed at (ax, ay) from the feet, the gardener going at (gvx, gvy). */
  private shed(spec: Shed, layer: 0 | 1 | 2, ax: number, ay: number, gvx: number, gvy: number): void {
    const pose = this.pose as FlairPose;
    const r = this.rand;
    const [bw, bh] = spec.box ?? [0, 0];
    const x = pose.x + ax + (r.next() - 0.5) * bw;
    const y = pose.y + ay + (r.next() - 0.5) * bh;
    const carry = spec.carry ?? 0;
    const bit: Bit = {
      spec,
      layer,
      x,
      y,
      vx: r.in(spec.v[0], spec.v[1]) * pose.facing + gvx * carry,
      vy: r.in(spec.v[2], spec.v[3]) + gvy * carry,
      hx: pose.x + ax,
      hy: pose.y + ay,
      age: 0,
      life: r.in(spec.life[0], spec.life[1]),
      seed: r.next() * 100,
    };
    if (this.bits.length >= MAX_BITS) this.bits.shift();
    this.bits.push(bit);
  }

  private age(h: number): void {
    const pose = this.pose as FlairPose;
    let kept = 0;
    for (const b of this.bits) {
      b.age += h;
      if (b.age >= b.life) continue;
      const s = b.spec;
      if (s.pull) {
        const [dx, dy] = [b.hx - b.x, b.hy - b.y];
        const d = Math.hypot(dx, dy) || 1;
        b.vx += (dx / d) * s.pull * h;
        b.vy += (dy / d) * s.pull * h;
      }
      b.vy += (s.g ?? 0) * h;
      if (s.drag !== undefined && s.drag !== 1) {
        const keep = s.drag ** h;
        b.vx *= keep;
        b.vy *= keep;
      }
      b.x += b.vx * h;
      b.y += b.vy * h;
      this.bits[kept++] = b;
      const t = b.age / b.life;
      const size = s.size[0] + (s.size[1] - s.size[0]) * t;
      const [a0, a1] = s.alpha ?? [1, 0];
      // Whole for most of its life, gone over its last third (a pixel that only fades reads as haze).
      const a = a0 + (a1 - a0) * Math.max(0, (t - 0.65) / 0.35);
      const wob = s.wobble ? Math.sin((b.age * s.wobble[1] + b.seed) * Math.PI * 2) * s.wobble[0] : 0;
      const [x, y] = [b.x - pose.x + wob, b.y - pose.y];
      const rgb = this.ramp(s.tones, t);
      if (s.glow) this.halo(b.layer, x, y, (size * s.glow) / 2, rgb, a * 0.35);
      this.dot(b.layer, x, y, size, rgb, a, s.add ?? false, s.tall ?? 1);
    }
    this.bits.length = kept;
  }

  // --- Chains ----------------------------------------------------------------------------------------------------------

  private swing(i: number, c: Chain, h: number, gvx: number, gvy: number): void {
    const pose = this.pose as FlairPose;
    const f = pose.facing;
    // Its root follows its anchor smoothly: the body's bob steps a few px between frames, which would whip the chain.
    const [tx, ty] = this.at(c.at);
    const was = this.chains.get(i);
    const ease = was ? 1 - Math.exp(-24 * h) : 1;
    const [ax, ay] = was ? [was.ax + (tx - was.ax) * ease, was.ay + (ty - was.ay) * ease] : [tx, ty];
    const [rx, ry] = [pose.x + ax, pose.y + ay];
    const sway = c.sway ? Math.sin((this.time * c.sway[1] + c.sway[2]) * Math.PI * 2) * c.sway[0] : 0;
    /** Link `k`'s place in the rest shape, from the root. */
    const rest = (k: number, into: [number, number]) => {
      let [x, y] = [0, 0];
      for (let j = 1; j <= k; j++) {
        const a = c.dir + sway + (c.curl ?? 0) * (j - 1);
        x += Math.cos(a) * c.seg * f;
        y += Math.sin(a) * c.seg;
      }
      // Bent along its length: each link as far as its own row says, past its root's.
      into[0] = rx + x + this.bent(c.at.bend, c.at.y + y) - this.bent(c.at.bend, c.at.y);
      into[1] = ry + y;
    };
    const tmp: [number, number] = [0, 0];
    let st = was;
    if (!st) {
      st = {
        p: new Float32Array((c.n + 1) * 2),
        q: new Float32Array((c.n + 1) * 2),
        shed: this.rand.next(),
        across: false,
        ax,
        ay,
      };
      for (let k = 0; k <= c.n; k++) {
        rest(k, tmp);
        st.p[k * 2] = st.q[k * 2] = tmp[0];
        st.p[k * 2 + 1] = st.q[k * 2 + 1] = tmp[1];
      }
      this.chains.set(i, st);
    }
    st.ax = ax;
    st.ay = ay;
    const { p, q } = st;
    if (h > 0) {
      const keep = (c.drag ?? 0.02) ** h;
      const hold = 1 - Math.exp(-c.stiff * h);
      p[0] = q[0] = rx;
      p[1] = q[1] = ry;
      for (let k = 1; k <= c.n; k++) {
        const [x, y] = [p[k * 2] as number, p[k * 2 + 1] as number];
        let nx = x + (x - (q[k * 2] as number)) * keep;
        let ny = y + (y - (q[k * 2 + 1] as number)) * keep + (c.g ?? 0) * h * h;
        if (c.wind) {
          const gust = Math.sin(this.time * 9 - k * 0.9 + i) + Math.sin(this.time * 5.3 + k * 0.5 + i * 2) * 0.6;
          nx += gust * c.wind * h * h * (k / c.n);
          ny += Math.cos(this.time * 7.1 - k * 0.7 + i) * c.wind * 0.5 * h * h * (k / c.n);
        }
        rest(k, tmp);
        nx += (tmp[0] - nx) * hold;
        ny += (tmp[1] - ny) * hold;
        q[k * 2] = x;
        q[k * 2 + 1] = y;
        p[k * 2] = nx;
        p[k * 2 + 1] = ny;
      }
      // Each link its length from the one before (twice: the tip settles).
      for (let pass = 0; pass < 2; pass++)
        for (let k = 1; k <= c.n; k++) {
          const [px, py] = [p[(k - 1) * 2] as number, p[(k - 1) * 2 + 1] as number];
          const [dx, dy] = [(p[k * 2] as number) - px, (p[k * 2 + 1] as number) - py];
          const d = Math.hypot(dx, dy) || 1;
          p[k * 2] = px + (dx / d) * c.seg;
          p[k * 2 + 1] = py + (dy / d) * c.seg;
        }
    }
    const layer = LAYER[c.layer];
    const width = (t: number) =>
      t < 0.5 ? c.w[0] + (c.w[1] - c.w[0]) * t * 2 : c.w[1] + (c.w[2] - c.w[1]) * (t * 2 - 1);
    const [ox, oy] = [pose.x, pose.y];
    if (c.shape === 'bars') {
      // Cloth: a bar of pixels a link, across the way the cloth runs (rows where it hangs, columns where it streams
      // out: there it hangs down from the chain, its top edge), a ripple running down it, so it reads as a sheet
      // whichever way it is blown.
      const thick = Math.ceil(c.seg) + 1;
      // Which way it runs, root to tip: it changes its mind only once well past the diagonal (no flicker between).
      const [run, drop] = [
        Math.abs((p[c.n * 2] as number) - (p[0] as number)),
        Math.abs((p[c.n * 2 + 1] as number) - (p[1] as number)),
      ];
      if (st.across ? drop > run * 1.5 : run > drop * 1.5) st.across = !st.across;
      const across = st.across;
      /** Link `k`'s bar: its top-left and its size. */
      const bar = (k: number): [number, number, number, number] => {
        const wide = Math.round(width(k / c.n));
        const ripple = Math.sin(this.time * 7 - k * 0.85 + i) * 1.6 * (k / c.n);
        const [x, y] = [(p[k * 2] as number) - ox, (p[k * 2 + 1] as number) - oy];
        return across
          ? [Math.round(x - thick / 2), Math.round(y - 3 + ripple), thick, wide]
          : [Math.round(x - wide / 2 + ripple), Math.round(y - thick / 2), wide, thick];
      };
      const hem = c.tatter ?? 0;
      /** Bar `k` in `rgb`, `grow` px bigger all round (its rim); the last one torn into teeth where `tatter` says. */
      const cloth = (k: number, grow: number, rgb: number) => {
        const [x, y, w, hgt] = bar(k);
        if (k < c.n || !hem)
          return this.put(layer, false, x - grow, y - grow, w + grow * 2, hgt + grow * 2, rgb, 1, false);
        // The hem torn: teeth of cloth with gaps between, each a little longer or shorter.
        const step = (across ? hgt : w) / hem;
        for (let t = 0; t < hem; t++) {
          const tooth = Math.max(1, Math.round(step * 0.6)) + grow * 2;
          const long = thick + ((t * 7 + i) % 3) + grow * 2;
          const at = Math.round(t * step) - grow;
          if (across) this.put(layer, false, x - grow - ((t * 7 + i) % 3), y + at, long, tooth, rgb, 1, false);
          else this.put(layer, false, x + at, y - grow, tooth, long, rgb, 1, false);
        }
      };
      if (c.rim !== undefined) for (let k = 0; k <= c.n; k++) cloth(k, 2, this.rgb(c.rim));
      for (let k = 0; k <= c.n; k++) cloth(k, 0, this.ramp(c.tones, k / c.n));
      if (c.lit !== undefined)
        for (let k = 0; k < c.n; k++) {
          // Its lit edge: down its left side where it hangs, along its top where it streams.
          const [x, y, w, hgt] = bar(k);
          if (across) this.put(layer, false, x, y + 1, w, 2, this.rgb(c.lit), 1, false);
          else this.put(layer, false, x + 1, y, 2, hgt, this.rgb(c.lit), 1, false);
        }
    } else {
      // A round tail: squares along it, close enough to leave no gap; its rim under them all.
      const pts: number[] = [];
      for (let k = 0; k < c.n; k++) {
        const w = width((k + 0.5) / c.n);
        const steps = Math.max(1, Math.ceil(c.seg / Math.max(1, w * 0.5)));
        for (let s = 0; s < steps; s++) {
          const t = s / steps;
          pts.push(
            (p[k * 2] as number) + ((p[k * 2 + 2] as number) - (p[k * 2] as number)) * t - ox,
            (p[k * 2 + 1] as number) + ((p[k * 2 + 3] as number) - (p[k * 2 + 1] as number)) * t - oy,
            (k + t) / c.n,
          );
        }
      }
      pts.push((p[c.n * 2] as number) - ox, (p[c.n * 2 + 1] as number) - oy, 1);
      if (c.rim !== undefined) {
        const rim = this.rgb(c.rim);
        for (let j = 0; j < pts.length; j += 3)
          this.dot(layer, pts[j] as number, pts[j + 1] as number, Math.round(width(pts[j + 2] as number)) + 4, rim);
      }
      for (let j = 0; j < pts.length; j += 3) {
        const t = pts[j + 2] as number;
        this.dot(layer, pts[j] as number, pts[j + 1] as number, Math.round(width(t)), this.ramp(c.tones, t));
      }
      if (c.lit !== undefined) {
        const lit = this.rgb(c.lit);
        for (let j = 0; j < pts.length; j += 3) {
          const w = Math.round(width(pts[j + 2] as number));
          if (w >= 4)
            this.dot(layer, (pts[j] as number) - w / 4, (pts[j + 1] as number) - w / 4, Math.max(1, w / 2 - 1), lit);
        }
      }
    }
    if (c.shed && h > 0) {
      st.shed += c.shed.rate * h;
      while (st.shed >= 1) {
        st.shed -= 1;
        const k = c.n - Math.floor(this.rand.next() * Math.min(3, c.n));
        this.shed(c.shed, layer, (p[k * 2] as number) - ox, (p[k * 2 + 1] as number) - oy, gvx, gvy);
      }
    }
  }

  // --- Orbits ----------------------------------------------------------------------------------------------------------

  private turn(i: number, o: Orbit, h: number, gvx: number, gvy: number): void {
    const [ax, ay] = this.at(o.at);
    const f = (this.pose as FlairPose).facing;
    const [ct, st] = [Math.cos((o.tilt ?? 0) * f), Math.sin((o.tilt ?? 0) * f)];
    const spin = (this.time / o.period) * Math.PI * 2;
    const tones = o.tones.map((t) => this.rgb(t));
    for (let k = 0; k < o.n; k++) {
      // Each its own place on the ring, its radius and brightness a little its own (`rough`).
      const own = Math.sin(k * 12.9898 + i * 78.233) * 43758.5453;
      const jitter = own - Math.floor(own);
      const rough = o.rough ?? 0;
      const base = spin + (k / o.n) * Math.PI * 2 + jitter * rough * 2;
      const r = 1 + (jitter - 0.5) * rough;
      const bob = o.bob ? Math.sin((this.time * o.bob[1] + jitter) * Math.PI * 2) * o.bob[0] : 0;
      const place = (a: number): [number, number, boolean] => {
        const [ex, ey] = [Math.cos(a) * o.rx * r, Math.sin(a) * o.ry * r];
        return [ax + ex * ct - ey * st, ay + ex * st + ey * ct + bob, Math.sin(a) > 0];
      };
      const flicker = rough ? 0.55 + 0.45 * Math.sin(this.time * (5 + jitter * 6) + jitter * 20) : 1;
      if (o.flame) {
        // A light that is a flame: standing on its place on the ring, leaning back from the way it goes.
        const [x, y, near] = place(base);
        const layer = o.layer ? LAYER[o.layer] : near ? 1 : 0;
        if (o.glow)
          this.halo(layer, x, y - o.flame[0] * 0.15, (o.size * o.glow) / 2, tones[1] ?? (tones[0] as number), 0.5);
        const going = -Math.sin(base) * Math.sign(o.period) * f;
        this.tongue(
          layer,
          x,
          y + o.flame[0] * 0.35,
          o.flame[0],
          o.flame[1],
          o.tones,
          2.4,
          i * 2.399 + k * 1.7,
          -going * o.flame[0] * 0.35,
        );
      }
      for (let t = o.flame ? -1 : tones.length - 1; t >= 0; t--) {
        const [x, y, near] = place(base - Math.sign(o.period) * t * (o.tail ?? 0.22));
        const layer = o.layer ? LAYER[o.layer] : near ? 1 : 0;
        const size = Math.max(1, o.size - t);
        if (t === 0 && o.glow) this.halo(layer, x, y, (o.size * o.glow) / 2, tones[0] as number, 0.4 * flicker);
        this.dot(layer, x, y, size, tones[t] as number, t === 0 ? flicker : 1 - t / (tones.length + 1));
      }
      if (o.shed && h > 0) {
        this.owed[i] = (this.owed[i] as number) + (o.shed.rate * h) / o.n;
        if ((this.owed[i] as number) >= 1) {
          this.owed[i] = (this.owed[i] as number) - 1;
          const [x, y, near] = place(base);
          this.shed(o.shed, o.layer ? LAYER[o.layer] : near ? 1 : 0, x, y, gvx, gvy);
        }
      }
    }
  }

  // --- Bolts -----------------------------------------------------------------------------------------------------------

  private strike(i: number, a: Arcs, h: number): void {
    let st = this.bolts.get(i);
    if (!st) {
      st = { next: this.rand.in(0, a.every[1]), live: [] };
      this.bolts.set(i, st);
    }
    st.next -= h;
    if (st.next <= 0 && a.pts.length >= 2) {
      st.next = this.rand.in(a.every[0], a.every[1]);
      const from = Math.floor(this.rand.next() * a.pts.length);
      const to = (from + 1 + Math.floor(this.rand.next() * (a.pts.length - 1))) % a.pts.length;
      const [x0, y0] = this.at(a.pts[from] as Anchor);
      const [x1, y1] = this.at(a.pts[to] as Anchor);
      // A jagged path: the straight line, each point of it pushed off sideways, least at its ends.
      const len = Math.hypot(x1 - x0, y1 - y0);
      const n = Math.max(2, Math.round(len / 4));
      const [nx, ny] = [-(y1 - y0) / (len || 1), (x1 - x0) / (len || 1)];
      const path: number[] = [];
      for (let k = 0; k <= n; k++) {
        const t = k / n;
        const off = (this.rand.next() - 0.5) * 2 * a.jag * Math.sin(Math.PI * t);
        path.push(x0 + (x1 - x0) * t + nx * off, y0 + (y1 - y0) * t + ny * off);
      }
      st.live.push({ path, age: 0 });
    }
    const layer = LAYER[a.layer];
    const [core, halo] = [this.rgb(a.tones[0]), this.rgb(a.tones[1])];
    st.live = st.live.filter((b) => {
      b.age += h;
      if (b.age > a.life) return false;
      // It flickers: there, gone a blink, there again fainter.
      const t = b.age / a.life;
      const seen = t < 0.4 || (t > 0.55 && t < 0.85);
      if (!seen) return true;
      const strength = t < 0.4 ? 1 : 0.6;
      for (let k = 0; k + 3 < b.path.length; k += 2) {
        const [xa, ya, xb, yb] = [b.path[k], b.path[k + 1], b.path[k + 2], b.path[k + 3]] as [
          number,
          number,
          number,
          number,
        ];
        const steps = Math.max(1, Math.ceil(Math.max(Math.abs(xb - xa), Math.abs(yb - ya))));
        for (let s = 0; s < steps; s++) {
          const [x, y] = [xa + ((xb - xa) * s) / steps, ya + ((yb - ya) * s) / steps];
          this.dot(layer, x, y, 3, halo, 0.55 * strength, true);
          this.dot(layer, x, y, 1, core, strength);
        }
      }
      if (a.glow) {
        const mid = (b.path.length >> 2) << 1;
        this.halo(layer, b.path[mid] as number, b.path[mid + 1] as number, a.glow, halo, 0.45 * strength);
      }
      return true;
    });
  }

  // --- Stars and glows -------------------------------------------------------------------------------------------------

  private shine(i: number, t: Twinkle): void {
    let st = this.stars.get(i);
    if (!st) {
      // Each star: where in the box (0..1), its period and its phase.
      st = new Float32Array(t.n * 4);
      for (let k = 0; k < t.n; k++)
        st.set([this.rand.next(), this.rand.next(), this.rand.in(t.period[0], t.period[1]), this.rand.next()], k * 4);
      this.stars.set(i, st);
    }
    const [ax, ay] = this.at(t.at);
    const f = (this.pose as FlairPose).facing;
    const [w, h] = t.box;
    const layer = LAYER[t.layer];
    for (let k = 0; k < t.n; k++) {
      const period = st[k * 4 + 2] as number;
      const phase = (this.time / period + (st[k * 4 + 3] as number)) % 1;
      const lit = Math.sin(phase * Math.PI);
      if (lit < 0.25) continue;
      const wrap = (v: number) => v - Math.floor(v);
      const u = wrap((st[k * 4] as number) + ((t.drift?.[0] ?? 0) * this.time) / w) - 0.5;
      const v = wrap((st[k * 4 + 1] as number) + ((t.drift?.[1] ?? 0) * this.time) / h) - 0.5;
      if (t.round && u * u + v * v > 0.25) continue;
      const [x, y] = [ax + u * w * f, ay + v * h];
      const rgb = this.rgb(t.tones[k % t.tones.length] as Tone);
      this.dot(layer, x, y, 1, rgb, lit, t.add ?? false);
      // At its brightest, a cross: a star, not a speck.
      if (lit > 0.9 && k % 3 === 0) {
        this.dot(layer, x - 1, y, 1, rgb, 0.6, t.add ?? false);
        this.dot(layer, x + 1, y, 1, rgb, 0.6, t.add ?? false);
        this.dot(layer, x, y - 1, 1, rgb, 0.6, t.add ?? false);
        this.dot(layer, x, y + 1, 1, rgb, 0.6, t.add ?? false);
      }
    }
  }

  /**
   * A flame standing on (ax, ay), in rows two art px high from its foot up: fat a quarter of the way up, narrowing to
   * its tip; each row shifted by its waver (more the higher) and by `lean` px at its tip, its width beating; its body
   * and heart inside its edge. Where its tip is.
   */
  private tongue(
    layer: 0 | 1 | 2,
    ax: number,
    ay: number,
    h: number,
    w: number,
    tones: readonly Tone[],
    speed: number,
    seed: number,
    lean: number,
  ): [number, number] {
    const t0 = this.time * speed;
    const tall = h * (0.82 + 0.18 * Math.sin(t0 * 5.1 + seed) + 0.1 * Math.sin(t0 * 11.3 + seed * 3));
    const rows = Math.max(2, Math.round(tall / 2));
    const [edge, body, heart] = [this.rgb(tones[0] as Tone), this.rgb(tones[1] as Tone), this.rgb(tones[2] as Tone)];
    let [tx, ty] = [ax, ay];
    for (let r = 0; r < rows; r++) {
      const t = (r + 0.5) / rows;
      // A teardrop: fat a quarter of the way up, full past its middle, then quickly to a point.
      const shape = t < 0.25 ? 0.72 + 0.28 * (t / 0.25) : 1 - ((t - 0.25) / 0.75) ** 1.7;
      const beat = 1 + 0.2 * t * Math.sin(t0 * 9.7 - t * 7 + seed);
      const half = w * shape * beat;
      const mid = ax + Math.sin(t0 * 4.3 + t * 2.2 + seed) * w * 0.3 * t + lean * t * t;
      const y = ay - (r + 1) * 2;
      const bar = (k: number, rgb: number) => {
        const wide = Math.round(half * 2 * k);
        if (wide >= 1) this.put(layer, false, Math.round(mid - wide / 2), Math.round(y), wide, 2, rgb, 1, false);
      };
      bar(1, edge);
      if (t < 0.78) bar(0.62, body);
      if (t < 0.46) bar(0.3, heart);
      [tx, ty] = [mid, y];
    }
    return [tx, ty];
  }

  /** A flame on its point, blown back by its gardener's run; flakes off its tip. */
  private burn(i: number, f: Flame, h: number, gvx: number, gvy: number): void {
    const [ax, ay] = this.at(f.at);
    const layer = LAYER[f.layer];
    const [tx, ty] = this.tongue(
      layer,
      ax,
      ay,
      f.h,
      f.w,
      f.tones,
      f.speed ?? 2,
      i * 2.399,
      -this.wind * (f.blown ?? 0),
    );
    if (f.shed && h > 0) {
      this.owed[i] = (this.owed[i] as number) + f.shed.rate * h;
      while ((this.owed[i] as number) >= 1) {
        this.owed[i] = (this.owed[i] as number) - 1;
        this.shed(f.shed, layer, tx, ty, gvx, gvy);
      }
    }
  }

  // --- Glyphs ----------------------------------------------------------------------------------------------------------

  private sign(i: number, g: Glyph, h: number): void {
    const r = this.rand;
    let st = this.signs.get(i);
    if (!st) {
      st = { frame: Math.floor(r.next() * g.frames.length), left: 0, hold: 1, u: 0, v: 0 };
      this.signs.set(i, st);
    }
    st.left -= h;
    if (st.left <= 0) {
      const n = g.frames.length;
      st.frame =
        g.order === 'random' && n > 1 ? (st.frame + 1 + Math.floor(r.next() * (n - 1))) % n : (st.frame + 1) % n;
      st.hold = st.left = r.in(g.hold[0], g.hold[1]);
      [st.u, st.v] = g.box ? [(r.next() - 0.5) * g.box[0], (r.next() - 0.5) * g.box[1]] : [0, 0];
    }
    if (g.shown !== undefined && st.left < st.hold * (1 - g.shown)) return;
    const f = (this.pose as FlairPose).facing;
    const frame = framesOf(g)[st.frame];
    if (!frame) return;
    const [ax, ay] = this.at(g.at, frame.h / 2);
    const [x0, y0] = [Math.round(ax + st.u * f), Math.round(ay + st.v)];
    const layer = LAYER[g.layer];
    if (g.glow) {
      const first = Object.values(g.tones)[0] as Tone;
      this.halo(layer, x0 + (frame.w / 2) * f, y0 + frame.h / 2, g.glow[0], this.rgb(first), g.glow[1]);
    }
    for (const [x, y, ch] of frame.px)
      this.put(
        layer,
        false,
        f > 0 ? x0 + x : x0 - x - 1,
        y0 + y,
        1,
        1,
        this.rgb(g.tones[ch] as Tone),
        1,
        g.add ?? false,
      );
  }

  private breathe(p: Pulse): void {
    const [x, y] = this.at(p.at);
    const k = 0.5 + 0.5 * Math.sin((this.time / p.period) * Math.PI * 2);
    this.halo(
      LAYER[p.layer],
      x,
      y,
      p.r[0] + (p.r[1] - p.r[0]) * k,
      this.rgb(p.tone),
      p.alpha[0] + (p.alpha[1] - p.alpha[0]) * k,
    );
  }
}
